// web/src/screens/admin/textset_editor.ts — v3.2 (migration 0038).
//
// Members editor for a named TextSet. v3.2 simplifies the previous "pick
// existing Text by short_code / create new Text" dance into a flat list of
// (content, gender) rows. Texts no longer have standalone identity — they
// are strict children of the parent TextSet.

import type { AdminTextSetMemberDTO } from "../../types/api.js";

const GENDERS = ["either", "male", "female"] as const;
type Gender = (typeof GENDERS)[number];

interface MemberRow {
  contentInput: HTMLInputElement;
  genderSelect: HTMLSelectElement;
}

export interface TextSetMembersEditorHandle {
  /** Replace the staged list — load existing members for display/edit. */
  setMembers(members: AdminTextSetMemberDTO[]): void;
  /** Returns the ordered member list ready for PATCH /textsets/{id}. */
  flush(): Promise<AdminTextSetMemberDTO[]>;
  unmount(): void;
}

function pillBtn(text: string, bg: string, fg: string): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.textContent = text;
  b.setAttribute(
    "style",
    `padding: 6px 12px; background: ${bg}; color: ${fg}; border: 0; border-radius: 4px; cursor: pointer; font-size: 13px;`,
  );
  return b;
}

function smallBtn(text: string, bg: string, fg: string): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.textContent = text;
  b.setAttribute(
    "style",
    `padding: 3px 8px; background: ${bg}; color: ${fg}; border: 0; border-radius: 4px; cursor: pointer; font-size: 12px;`,
  );
  return b;
}

export function mountTextSetMembersEditor(
  host: HTMLElement,
  opts: { title?: string } = {},
): TextSetMembersEditorHandle {
  let rows: MemberRow[] = [];

  const section = document.createElement("section");
  section.setAttribute(
    "style",
    "display: flex; flex-direction: column; gap: 8px; padding: 10px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px;",
  );
  const titleEl = document.createElement("h3");
  titleEl.textContent = opts.title ?? "Members";
  titleEl.setAttribute("style", "margin: 0; font-size: 14px; color: #111827;");
  const note = document.createElement("p");
  note.textContent = "Ordered list — narration plays members in order; random pickers ignore the order. Saving replaces the full member list.";
  note.setAttribute("style", "margin: 0; font-size: 12px; color: #6b7280;");
  const list = document.createElement("ol");
  list.setAttribute(
    "style",
    "list-style: decimal inside; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 6px;",
  );
  const btnRow = document.createElement("div");
  btnRow.setAttribute("style", "display: flex; gap: 6px; align-items: center; margin-top: 4px;");
  const addBtn = pillBtn("+ Add member", "#16a34a", "white");
  btnRow.append(addBtn);
  section.append(titleEl, note, list, btnRow);
  host.replaceChildren(section);

  function moveRow(idx: number, delta: number): void {
    const j = idx + delta;
    if (j < 0 || j >= rows.length) return;
    const tmp = rows[idx]!;
    rows[idx] = rows[j]!;
    rows[j] = tmp;
    renderRows();
  }

  function removeRow(idx: number): void {
    rows.splice(idx, 1);
    renderRows();
  }

  function renderRows(): void {
    list.replaceChildren();
    rows.forEach((r, idx) => {
      const li = document.createElement("li");
      li.setAttribute(
        "style",
        "display: flex; gap: 6px; align-items: center; padding: 4px 8px; background: white; border: 1px solid #e5e7eb; border-radius: 4px;",
      );
      // content input flexes to fill row
      r.contentInput.style.flex = "1 1 0";
      r.contentInput.style.minWidth = "0";
      li.appendChild(r.contentInput);
      li.appendChild(r.genderSelect);

      const reorder = document.createElement("span");
      reorder.setAttribute("style", "display: inline-flex; gap: 4px;");
      const up = smallBtn("↑", "#e5e7eb", "#111827");
      up.disabled = idx === 0;
      up.addEventListener("click", () => moveRow(idx, -1));
      const down = smallBtn("↓", "#e5e7eb", "#111827");
      down.disabled = idx === rows.length - 1;
      down.addEventListener("click", () => moveRow(idx, +1));
      const rm = smallBtn("✕", "#dc2626", "white");
      rm.addEventListener("click", () => removeRow(idx));
      reorder.append(up, down, rm);
      li.appendChild(reorder);
      list.appendChild(li);
    });
  }

  function makeRow(content: string, gender: Gender): MemberRow {
    const input = document.createElement("input");
    input.type = "text";
    input.value = content;
    input.placeholder = "Member content (LV)";
    input.lang = "lv";
    input.spellcheck = false;
    input.setAttribute(
      "style",
      "padding: 6px 10px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 14px;",
    );
    const sel = document.createElement("select");
    sel.setAttribute(
      "style",
      "padding: 6px 10px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 13px; background: white;",
    );
    for (const g of GENDERS) {
      const opt = document.createElement("option");
      opt.value = g;
      opt.textContent = g;
      sel.appendChild(opt);
    }
    sel.value = gender;
    return { contentInput: input, genderSelect: sel };
  }

  addBtn.addEventListener("click", () => {
    rows.push(makeRow("", "either"));
    renderRows();
    rows[rows.length - 1]?.contentInput.focus();
  });

  function setMembers(members: AdminTextSetMemberDTO[]): void {
    rows = members
      .slice()
      .sort((a, b) => a.order_index - b.order_index)
      .map((m) => makeRow(m.content ?? "", (GENDERS.includes((m.gender ?? "either") as Gender) ? (m.gender as Gender) : "either")));
    renderRows();
  }

  async function flush(): Promise<AdminTextSetMemberDTO[]> {
    return rows
      .map((r, idx) => ({
        order_index: idx,
        content: r.contentInput.value.trim(),
        gender: r.genderSelect.value,
      }))
      .filter((m) => m.content !== "");
  }

  return {
    setMembers,
    flush,
    unmount(): void { host.replaceChildren(); },
  };
}
